/**
 * Hero phone with cinematic 3D tilt, halo glow + glass reflection.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { phoneFrame, screenContent } from "./_mockHelpers.js";

export const phoneTilt3D: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const caption = inputs.subtext ?? "";
  const isVertical = ctx.format === "9:16";
  const phoneW = isVertical ? 460 : 380;

  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const capId = `${clipPrefix}-cap`;
  const sceneId = `${clipPrefix}-stage`;
  const deviceId = `${clipPrefix}-device`;
  const haloId = `${clipPrefix}-halo`;
  const reflId = `${clipPrefix}-reflection`;
  const ring1Id = `${clipPrefix}-ring1`;
  const ring2Id = `${clipPrefix}-ring2`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const inner = screenContent({ media: inputs.media, accent, fallback: "app" });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:28px;padding:72px;perspective:2200px;">
    ${caption ? `<div id="${capId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.8}px;letter-spacing:7px;text-transform:uppercase;color:${accent};">${escapeHtml(caption)}</div>` : ""}
    ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.78}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.02;text-align:center;text-wrap:balance;max-width:${isVertical ? 820 : 1100}px;">${escapeHtml(headline)}</div>` : ""}
    <div id="${sceneId}" style="position:relative;display:flex;justify-content:center;align-items:center;margin-top:8px;transform-style:preserve-3d;">
      <div id="${ring1Id}" style="position:absolute;width:${phoneW * 2.4}px;height:${phoneW * 2.4}px;border-radius:9999px;border:1.5px solid ${withAlpha(accent, 0.35)};box-shadow:0 0 80px ${withAlpha(accent, 0.18)} inset;"></div>
      <div id="${ring2Id}" style="position:absolute;width:${phoneW * 1.8}px;height:${phoneW * 1.8}px;border-radius:9999px;border:1.5px dashed ${withAlpha(accent, 0.5)};"></div>
      <div id="${haloId}" style="position:absolute;width:${phoneW * 1.5}px;height:${phoneW * 1.5}px;border-radius:9999px;background:radial-gradient(closest-side, ${withAlpha(accent, 0.55)} 0%, transparent 70%);filter:blur(28px);"></div>
      <div style="position:relative;transform-style:preserve-3d;">
        ${phoneFrame({ id: deviceId, width: phoneW, accent, inner })}
        <div id="${reflId}" style="position:absolute;inset:0;border-radius:${Math.round(phoneW * 0.13)}px;background:linear-gradient(115deg, ${withAlpha(themeColors.fg, 0.28)} 0%, transparent 35%, transparent 65%, ${withAlpha(themeColors.fg, 0.12)} 100%);pointer-events:none;mix-blend-mode:screen;"></div>
      </div>
    </div>
  </div>`;

  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 16 }) : "",
    caption ? gsapSetLine(`#${capId}`, { opacity: 0, y: 12 }) : "",
    gsapSetLine(`#${sceneId}`, { opacity: 0, rotationY: -28, rotationX: 8, scale: 0.88, transformOrigin: "50% 50%" }),
    gsapSetLine(`#${haloId}`, { opacity: 0, scale: 0.7 }),
    gsapSetLine(`#${ring1Id}`, { opacity: 0, scale: 0.7 }),
    gsapSetLine(`#${ring2Id}`, { opacity: 0, scale: 0.7 }),
    gsapSetLine(`#${reflId}`, { opacity: 0 }),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${sceneId}`, { opacity: 1, rotationY: -8, rotationX: 4, scale: 1, duration: 1.15, ease: "raw:\"power3.out\"" }, 0.05),
    t.to(`#${sceneId}`, { rotationY: 8, duration: Math.max(2.0, clipDurationSec - 1.6), ease: "raw:\"sine.inOut\"" }, 1.2),
    t.to(`#${haloId}`, { opacity: 0.9, scale: 1, duration: 0.85, ease: "raw:\"power2.out\"" }, 0.2),
    t.to(`#${ring1Id}`, { opacity: 1, scale: 1, duration: 1.0, ease: "raw:\"power2.out\"" }, 0.3),
    t.to(`#${ring2Id}`, { opacity: 1, scale: 1, duration: 1.0, ease: "raw:\"power2.out\"" }, 0.45),
    t.to(`#${ring2Id}`, { rotation: 360, duration: clipDurationSec, ease: "raw:\"none\"" }, 0),
    t.fromTo(`#${reflId}`, { opacity: 0.35 }, { opacity: 0.65, duration: Math.max(1.2, clipDurationSec - 1.0), ease: "raw:\"sine.inOut\"" }, 0.6),
  ];
  if (caption) tweens.push(t.to(`#${capId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.1));
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.3));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
